import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { uploadAttachment } from "../utils/attachmentUpload";

const MAX_FILES = 10;

function kindForType(type) {
  const t = (type || "").toLowerCase();
  if (t.startsWith("image/")) return "image";
  if (t.startsWith("video/")) return "video";
  if (t === "application/pdf") return "document";
  return "other";
}

let localIdSeq = 0;

// Self-contained attachment picker for the initial car request (uploads as thread-less "request"
// attachments). Reports { attachmentIds, uploading, count } to the parent via onChange; expose a
// reset() so the parent can clear it after a successful send.
const RequestAttachments = forwardRef(function RequestAttachments({ token, onChange }, ref) {
  const [files, setFiles] = useState([]);
  const inputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Mirror of `files` so side-effect code (cap check, unmount cleanup) reads the latest list
  // without putting impure logic inside a state updater.
  const filesRef = useRef(files);
  filesRef.current = files;

  // Revoke any object URLs on unmount.
  useEffect(() => {
    return () => {
      filesRef.current.forEach((f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
    };
  }, []);

  // Report derived state to the parent whenever the file list changes.
  useEffect(() => {
    const attachmentIds = files
      .filter((f) => f.status === "done" && f.attachmentId)
      .map((f) => f.attachmentId);
    const uploading = files.some((f) => f.status === "uploading");
    onChangeRef.current?.({ attachmentIds, uploading, count: files.length });
  }, [files]);

  const reset = useCallback(() => {
    filesRef.current.forEach((f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
    setFiles([]);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  useImperativeHandle(ref, () => ({ reset }), [reset]);

  const startUpload = useCallback(
    (entry) => {
      uploadAttachment(entry.file, { token })
        .then((attachmentId) => {
          setFiles((prev) =>
            prev.map((f) => (f.localId === entry.localId ? { ...f, status: "done", attachmentId } : f))
          );
        })
        .catch((err) => {
          setFiles((prev) =>
            prev.map((f) =>
              f.localId === entry.localId
                ? { ...f, status: "error", error: err?.message || "Upload failed." }
                : f
            )
          );
        });
    },
    [token]
  );

  const onPick = useCallback(
    (event) => {
      const picked = Array.from(event.target.files || []);
      if (inputRef.current) inputRef.current.value = "";
      if (!picked.length) return;

      // Compute entries (with their side effects) OUTSIDE the state updater, so React StrictMode's
      // double-invocation of the updater can never trigger duplicate uploads.
      const room = Math.max(0, MAX_FILES - filesRef.current.length);
      if (room <= 0) return;
      const accepted = picked.slice(0, room).map((file) => {
        const kind = kindForType(file.type);
        return {
          localId: ++localIdSeq,
          file,
          name: file.name,
          kind,
          status: "uploading",
          attachmentId: null,
          error: "",
          previewUrl: kind === "image" ? URL.createObjectURL(file) : null,
        };
      });
      setFiles((prev) => [...prev, ...accepted]);
      accepted.forEach((entry) => startUpload(entry));
    },
    [startUpload]
  );

  const removeFile = useCallback((localId) => {
    setFiles((prev) => {
      const target = prev.find((f) => f.localId === localId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((f) => f.localId !== localId);
    });
  }, []);

  return (
    <div className="space-y-2">
      <p className="text-xs text-blue-900 dark:text-blue-200">
        Adding clear photos or a short video of the problem helps shops give you a more accurate quote.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={files.length >= MAX_FILES}
          className="rounded-lg border border-blue-600 px-3 py-1.5 text-sm font-semibold text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Add photos or video
        </button>
        {files.length > 0 && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {files.length}/{MAX_FILES} added
          </span>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*,video/*,application/pdf"
          multiple
          onChange={onPick}
          className="hidden"
          aria-label="Add attachments to your request"
        />
      </div>

      {files.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {files.map((f) => (
            <li
              key={f.localId}
              className="flex items-center gap-2 rounded-lg border bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-800 dark:text-gray-100"
            >
              {f.previewUrl ? (
                <img src={f.previewUrl} alt="" className="h-8 w-8 rounded object-cover" />
              ) : (
                <span className="flex h-8 w-8 items-center justify-center rounded bg-slate-100 dark:bg-gray-800 text-[10px] font-semibold uppercase text-slate-500 dark:text-gray-400">
                  {f.kind === "video" ? "Vid" : f.kind === "document" ? "PDF" : "File"}
                </span>
              )}
              <span className="max-w-[10rem] truncate" title={f.name}>{f.name}</span>
              {f.status === "uploading" && <span className="text-blue-700 dark:text-blue-300">Uploading...</span>}
              {f.status === "error" && <span className="text-red-600 dark:text-red-400" title={f.error}>Failed</span>}
              <button
                type="button"
                onClick={() => removeFile(f.localId)}
                className="text-slate-400 hover:text-red-600"
                aria-label={`Remove ${f.name}`}
              >
                x
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

export default RequestAttachments;
